import { Component, computed, input, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CalendarEvent, DATETIMEFORMART } from 'src/app/interfaces/interfaces';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';

interface CalendarEventWithPosition extends CalendarEvent {
  position: {
    top: string;
    height: string;
    left: string;
    width: string;
  };
  zIndex?: number;
}

@Component({
  selector: 'app-daily-calendar-modal',
  templateUrl: './daily-calendar-modal.page.html',
  styleUrls: ['./daily-calendar-modal.page.scss'],
})
export class DailyCalendarModalPage implements OnInit {
  // Input signals
  events = input<CalendarEvent[]>([]);
  selectedDate: WritableSignal<Date> = signal(new Date());

  locale = this.dateTimeFormatService.selectedDateTimeFormat;
  
  // Time range for the calendar (5 AM to 11 PM)
  startHour = 5;
  endHour = 23;
  
  // Slot height for consistent positioning
  slotHeight = 60; // Default desktop height
  
  // Computed signals
  currentTime: Date = new Date();
  currentTimePosition: number = this.calculateCurrentTimePosition();
  
  
  // Week days for mini calendar
  weekDays: Signal<any[]> = computed(() => this.generateWeekDays());
  
  // Events for selected date
  selectedDateEvents: Signal<any[]> = computed(() => this.getEventsForDate(this.selectedDate(), this.events()));
  
  // Time slots for the calendar
  timeSlots: Signal<any[]> = computed(() => this.generateTimeSlots());
  
  // Check if today is selected
  isTodaySelected: Signal<boolean> = computed(() => {
    const selectedDate = this.selectedDate();
    const today = moment().startOf('day');
    return moment(selectedDate).isSame(today, 'day');
  });

  constructor(
    private modalController: ModalController,
    private dateTimeFormatService: DatetimeFormatService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Update current time position when component initializes
    this.currentTimePosition = this.calculateCurrentTimePosition();
  }


  private generateWeekDays(): any[] {
    const today = moment().startOf('day');
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = moment(today).add(i, 'days');
      days.push({
        name: day.format('ddd'),
        number: day.format('D'),
        date: day.toDate(),
        isSelected: day.isSame(this.selectedDate(), 'day')
      });
    }
    
    return days;
  }

  private generateTimeSlots(): any[] {
    const slots = [];
    for (let hour = this.startHour; hour <= this.endHour; hour++) {
      // Add hour mark
      const hourPosition = (hour - this.startHour) * this.slotHeight;
      slots.push({
        hour: hour,
        time: `${hour.toString().padStart(2, '0')}:00`,
        displayTime: `${hour.toString().padStart(2, '0')}:00`,
        position: hourPosition,
        positionPx: `${hourPosition}px`,
        isHalfHour: false
      });
      
      // Add 30-minute mark (except for the last hour)
      if (hour < this.endHour) {
        const halfHourPosition = (hour - this.startHour) * this.slotHeight + (this.slotHeight / 2);
        slots.push({
          hour: hour,
          time: `${hour.toString().padStart(2, '0')}:30`,
          displayTime: `${hour.toString().padStart(2, '0')}:30`,
          position: halfHourPosition,
          positionPx: `${halfHourPosition}px`,
          isHalfHour: true
        });
      }
    }
    // Sort slots by position
    return slots.sort((a, b) => a.position - b.position);
  }

  private getEventsForDate(date: Date, events: CalendarEvent[]): any[] {
    // Use local time for consistent timezone handling
    const startOfDay = moment(date).startOf('day');
    const endOfDay = moment(date).endOf('day');

    // First, filter events for the selected date
    const filteredEvents = events.filter(event => {
      // if (event.isExpired) return false;
      
      const eventStart = moment(event.startTime);
      const eventEnd = moment(event.endTime);
      
      // Check if event overlaps with the selected date
      return eventStart.isBefore(endOfDay) && eventEnd.isAfter(startOfDay);
    }).sort((a, b) => moment(a.startTime).diff(moment(b.startTime)));

    // Calculate positions for all events in one pass to handle overlaps correctly
    const eventsWithPositions = this.calculateEventPositions(filteredEvents);

    return eventsWithPositions.map((event: CalendarEventWithPosition) => {
      return {
        ...event,
        color: event.isOrganizer ? '#4CAF50' : '#ed143d',
        backgroundColor: event.isOrganizer ? '#4CAF5022' : '#ed143d22',
        formattedTime: event.startTime + ' - ' + event.endTime,
        location: event.place?.displayName || 'No location',
        left: event.position.left,
        width: event.position.width,
        height: event.position.height,
        top: event.position.top,
        showTime: this.shouldShowEventTime(event),
        showLocation: this.shouldShowEventLocation(event),
      }
    });
  }

  private calculateEventPositions(events: CalendarEvent[]): CalendarEventWithPosition[] {
    if (!events || events.length === 0) return [];

    // Sort by duration asc, then start time asc (place shortest first)
    const eventsSorted = [...events].sort((a, b) => {
      const aStart = moment(a.startTime);
      const aEnd = moment(a.endTime);
      const bStart = moment(b.startTime);
      const bEnd = moment(b.endTime);
      const aDur = aEnd.diff(aStart, 'minutes');
      const bDur = bEnd.diff(bStart, 'minutes');
      if (aDur !== bDur) return bDur - aDur;
      return aStart.valueOf() - bStart.valueOf();
    });

    const baseLeftOffset = 40;
    const containerWidth = window.innerWidth - 20;
    const availableWidth = containerWidth - baseLeftOffset;
    const indentWidth = 8;

    // 1) Assign columns based on first-hour overlap rule
    const columnById = new Map<string, number>();
    for (const ev of eventsSorted) {
      let col = 0;
      while (true) {
        const conflict = Array.from(columnById.entries()).some(([otherId, otherCol]) => {
          if (otherCol !== col) return false;
          const other = events.find(e => e.id === otherId)!;
          return this.isOverlapWithinFirstHour(ev, other);
        });
        if (!conflict) break;
        col++;
      }
      columnById.set(ev.id, col);
    }

    const numColumns = (Array.from(columnById.values()).reduce((max, v) => Math.max(max, v), 0)) + 1;
    const columnWidth = availableWidth / Math.max(1, numColumns);

    // 2) For each event, compute within-column late-overlap stacking indent
    const result: CalendarEventWithPosition[] = [];
    for (const ev of eventsSorted) {
      const evStart = moment(ev.startTime);
      const evEnd = moment(ev.endTime);

      const startMinutes = (evStart.hour() - this.startHour) * 60 + evStart.minute();
      const endMinutes = (evEnd.hour() - this.startHour) * 60 + evEnd.minute();
      
      // Clamp startMinutes to 0 if event starts before the calendar start time (5 AM)
      const clampedStartMinutes = Math.max(0, startMinutes);
      const topPx = (clampedStartMinutes / 60) * this.slotHeight;
      
      // Adjust height to account for clamped start time
      const adjustedEndMinutes = Math.max(clampedStartMinutes, endMinutes);
      const heightPx = ((adjustedEndMinutes - clampedStartMinutes) / 60) * this.slotHeight;

      const col = columnById.get(ev.id) ?? 0;

      const lateOverlapLevel = eventsSorted.filter(other => {
        if (other.id === ev.id) return false;
        const otherCol = columnById.get(other.id) ?? -1;
        if (otherCol !== col) return false;
        const otherStart = moment(other.startTime);
        const otherEnd = moment(other.endTime);
        if (otherStart.isAfter(evStart)) return false; // only earlier or equal start
        const activeAtStart = evStart.isBefore(otherEnd);
        if (!activeAtStart) return false;
        // stack only when overlap starts after the first hour of the earlier event
        return evStart.isSameOrAfter(moment(otherStart).add(1, 'hour'));
      }).length;

      const indentPx = indentWidth * Math.max(0, lateOverlapLevel);

      // Expand width to the right until the next column that has a full-duration overlap with this event.
      const blockingRightCols: number[] = eventsSorted
        .filter(other => other.id !== ev.id)
        .filter(other => {
          const otherCol = columnById.get(other.id) ?? -1;
          if (otherCol <= col) return false;
          const otherStart = moment(other.startTime);
          const otherEnd = moment(other.endTime);
          return evStart.isBefore(otherEnd) && evEnd.isAfter(otherStart);
        })
        .map(other => columnById.get(other.id) as number);

      const nextBlockingCol = blockingRightCols.length > 0 ? Math.min(...blockingRightCols) : null;
      const lastSpanCol = nextBlockingCol != null ? Math.max(col, nextBlockingCol - 1) : (numColumns - 1);
      const spanColumns = Math.max(1, lastSpanCol - col + 1);

      const leftPx = baseLeftOffset + col * columnWidth + indentPx;
      const widthPx = Math.max(16, spanColumns * columnWidth - indentPx);

      const zIndex = 5 + lateOverlapLevel;

      result.push({
        ...ev,
        position: {
          top: `${topPx}px`,
          height: `${heightPx}px`,
          left: `${leftPx}px`,
          width: `${widthPx}px`
        },
        zIndex
      });
    }

    return result;
  }

  private isOverlapWithinFirstHour(a: CalendarEvent, b: CalendarEvent): boolean {
    const aStart = moment(a.startTime);
    const aEnd = moment(a.endTime);
    const bStart = moment(b.startTime);
    const bEnd = moment(b.endTime);

    // Determine earlier and later events by start time
    const earlierStartsFirst = aStart.isSameOrBefore(bStart);
    const earlierStart = earlierStartsFirst ? aStart : bStart;
    const earlierEnd = earlierStartsFirst ? aEnd : bEnd;
    const laterStart = earlierStartsFirst ? bStart : aStart;
    const laterEnd = earlierStartsFirst ? bEnd : aEnd;

    // Only consider the first hour window of the earlier event
    const firstHourEnd = moment(earlierStart).add(1, 'hour');

    // Compute actual overlap interval
    const overlapStart = moment.max(earlierStart, laterStart);
    const overlapEnd = moment.min(earlierEnd, laterEnd);

    // Overlap exists and begins within the first hour of the earlier event
    return overlapStart.isBefore(overlapEnd) && overlapStart.isBefore(firstHourEnd);
  }



  private calculateCurrentTimePosition(): number {
    const now = moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();
    
    // Only show current time if it's within our calendar range
    if (currentHour < this.startHour || currentHour > this.endHour) {
      return -1;
    }
    
    // Calculate position in pixels to match time slot structure
    const totalMinutes = (currentHour - this.startHour) * 60 + currentMinute;
    
    // Convert minutes to pixels (60 minutes = slotHeight px)
    return (totalMinutes / 60) * this.slotHeight;
  }

  selectDate(date: Date) {
    this.selectedDate.set(date);
  }

  async closeModal() {
    await this.modalController.dismiss();
  }


  getEventDuration(event: CalendarEvent): number {
    const eventStart = moment(event.startTime);
    const eventEnd = moment(event.endTime);
    return eventEnd.diff(eventStart, 'minutes');
  }

  getEventDurationInHours(event: CalendarEvent): number {
    const eventStart = moment(event.startTime);
    const eventEnd = moment(event.endTime);
    return eventEnd.diff(eventStart, 'hours', true); // true for decimal precision
  }

  shouldShowEventTime(event: CalendarEvent): boolean {
    return this.getEventDuration(event) >= 45;
  }

  shouldShowEventLocation(event: CalendarEvent): boolean {
    return this.getEventDuration(event) >= 60;
  }

}
